// Counts what app.getAppMetrics() cannot see: how many capture tracks and audio contexts are still
// alive, and how much cross-window sync traffic the master pushes. Armed from the hidden dev menu —
// patching globals is not something a shipped session should be doing.

interface PerfBridge {
  isEnabled(): Promise<boolean>;
  setEnabled(enabled: boolean): Promise<boolean>;
  mark(name: string, data?: Record<string, unknown>): void;
}

interface PerfHost {
  perf?: PerfBridge;
  hasArgument?(flag: string): boolean;
}

const INTERVAL_MS = 5000;
const SURFACES = ['island', 'panel', 'chat', 'briefing', 'audiopicker'] as const;

let tick: (() => Promise<void>) | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function bridge(): PerfBridge | undefined {
  return (window as unknown as { electronAPI?: PerfHost }).electronAPI?.perf;
}

/** Join a capture that is already running — an overlay window opened after the toggle was flipped. */
export async function startPerfProbe(): Promise<void> {
  const perf = bridge();
  if (!perf || timer || !(await perf.isEnabled())) return;
  run(perf);
}

/** The dev-menu switch: turns the main-process sampler and this renderer's probe on together. */
export async function setPerfTelemetry(enabled: boolean): Promise<boolean> {
  const perf = bridge();
  if (!perf) return false;

  const running = await perf.setEnabled(enabled);
  if (running) run(perf);
  else if (timer) {
    clearInterval(timer);
    timer = null;
  }
  return running;
}

function run(perf: PerfBridge): void {
  tick ??= install(perf);
  timer ??= setInterval(() => void tick?.(), INTERVAL_MS);
}

// The global patches are one-way: install once, then only the sampling timer is toggled. That is
// safe because the switch lives in pre-session, so nothing has captured audio yet.
function install(perf: PerfBridge): () => Promise<void> {
  const host = (window as unknown as { electronAPI?: PerfHost }).electronAPI;

  // WeakRefs, so the probe itself never keeps a track or context alive and turns the measurement
  // into the leak it is looking for.
  const tracks = new Set<WeakRef<MediaStreamTrack>>();
  const contexts = new Set<WeakRef<BaseAudioContext>>();

  const media = navigator.mediaDevices;
  for (const key of ['getUserMedia', 'getDisplayMedia'] as const) {
    const original = media[key].bind(media);
    media[key] = async (constraints?: MediaStreamConstraints & DisplayMediaStreamOptions): Promise<MediaStream> => {
      const stream = await original(constraints);
      for (const track of stream.getTracks()) tracks.add(new WeakRef(track));
      return stream;
    };
  }

  const NativeAudioContext = window.AudioContext;
  window.AudioContext = class extends NativeAudioContext {
    constructor(options?: AudioContextOptions) {
      super(options);
      contexts.add(new WeakRef(this));
    }
  };

  // Long Animation Frames: the AEC3 ScriptProcessorNode callback shares the main thread, so a starved
  // echo pipeline shows up here before anyone hears it.
  let loafCount = 0;
  let loafBlockingMs = 0;
  let loafLongestMs = 0;
  try {
    new PerformanceObserver(list => {
      for (const entry of list.getEntries() as (PerformanceEntry & { blockingDuration?: number })[]) {
        loafCount += 1;
        loafBlockingMs += entry.blockingDuration ?? 0;
        loafLongestMs = Math.max(loafLongestMs, entry.duration);
      }
    }).observe({ type: 'long-animation-frame' } as PerformanceObserverInit);
  } catch {
    // Chromium below 123 has no long-animation-frame entry type.
  }

  const peers = new Set<WeakRef<RTCPeerConnection>>();
  const NativePeerConnection = window.RTCPeerConnection;
  window.RTCPeerConnection = class extends NativePeerConnection {
    constructor(config?: RTCConfiguration) {
      super(config);
      peers.add(new WeakRef(this));
    }
  };
  // Per connection, per source: stat ids are only unique inside their own report.
  const captureSeen = new WeakMap<RTCPeerConnection, Map<string, { duration: number; at: number }>>();

  let syncMessages = 0;
  let lastSyncPayload: unknown = null;
  const nativePostMessage = BroadcastChannel.prototype.postMessage;
  BroadcastChannel.prototype.postMessage = function (message: unknown): void {
    syncMessages += 1;
    lastSyncPayload = message;
    nativePostMessage.call(this, message);
  };

  // The launch argument, not location.search: a dev-server reload drops the query and every overlay
  // then reports itself as "main", silently merging four windows into one row of the report.
  const surface = SURFACES.find(name => host?.hasArgument?.(`--surface=${name}`)) ?? new URLSearchParams(location.search).get('surface') ?? 'main';

  // totalSamplesDuration is the audio the capture pipeline actually produced, so the gap against
  // elapsed time is the transcript hole measured at its source rather than inferred from lost words.
  const captureShortfallPct = async (): Promise<number | undefined> => {
    let worst: number | undefined;
    for (const ref of peers) {
      const peer = ref.deref();
      if (!peer) {
        peers.delete(ref);
        continue;
      }
      let report: RTCStatsReport;
      try {
        report = await peer.getStats();
      } catch {
        continue;
      }
      const seen = captureSeen.get(peer) ?? new Map<string, { duration: number; at: number }>();
      captureSeen.set(peer, seen);
      const now = performance.now();
      report.forEach(stat => {
        const source = stat as { id: string; type: string; kind?: string; totalSamplesDuration?: number };
        if (source.type !== 'media-source' || source.kind !== 'audio' || source.totalSamplesDuration === undefined) return;
        const previous = seen.get(source.id);
        seen.set(source.id, { duration: source.totalSamplesDuration, at: now });
        if (!previous || now <= previous.at) return;
        const shortfall = Math.round((1 - ((source.totalSamplesDuration - previous.duration) * 1000) / (now - previous.at)) * 1000) / 10;
        if (worst === undefined || shortfall > worst) worst = shortfall;
      });
    }
    return worst;
  };

  return async (): Promise<void> => {
    let liveTracks = 0;
    let liveVideoTracks = 0;
    let endedTracks = 0;
    for (const ref of tracks) {
      const track = ref.deref();
      if (!track) {
        tracks.delete(ref);
        continue;
      }
      if (track.readyState !== 'live') {
        endedTracks += 1;
        continue;
      }
      liveTracks += 1;
      if (track.kind === 'video') liveVideoTracks += 1;
    }

    let openAudioContexts = 0;
    for (const ref of contexts) {
      const context = ref.deref();
      if (!context) {
        contexts.delete(ref);
        continue;
      }
      if (context.state !== 'closed') openAudioContexts += 1;
    }

    // Once per tick, so the stringify cost is irrelevant next to the 20 Hz broadcast it measures.
    let syncLastBytes: number | undefined;
    try {
      syncLastBytes = lastSyncPayload === null ? undefined : JSON.stringify(lastSyncPayload).length;
    } catch {
      syncLastBytes = undefined;
    }
    // Drop the reference: an idle app would otherwise pin the last payload's whole object graph.
    lastSyncPayload = null;

    const memory = (performance as Performance & { memory?: { usedJSHeapSize: number; totalJSHeapSize: number } }).memory;
    const captureShortfall = await captureShortfallPct();

    perf.mark('renderer', {
      surface,
      loafCount,
      loafBlockingMs: Math.round(loafBlockingMs),
      loafLongestMs: Math.round(loafLongestMs),
      captureShortfall,
      heapKb: memory && Math.round(memory.usedJSHeapSize / 1024),
      heapTotalKb: memory && Math.round(memory.totalJSHeapSize / 1024),
      liveTracks,
      liveVideoTracks,
      endedTracks,
      openAudioContexts,
      syncMessages,
      syncLastBytes,
      domNodes: document.getElementsByTagName('*').length,
    });

    syncMessages = 0;
    loafCount = 0;
    loafBlockingMs = 0;
    loafLongestMs = 0;
  };
}
